import type { Translations } from '../types';

export const de: Translations = {
    identityVerification: {
        title: 'Bestätigen Sie Ihre Identität',
        mockNotice:
            'Testmodus. Evrotrust ist nicht verbunden. Es werden keine Ausweisdokumente oder biometrischen Daten erfasst.',
        consent: 'Ich verstehe, dass dies ein Test ist, und stimme dem Speichern eines Testergebnisses zu.',
        continue: 'Prüfung fortsetzen',
        help: 'Bei Fragen wenden Sie sich an Ihren Unternehmensvertreter.',
        expires: 'Einladung gültig bis {date}',
        invalidTitle: 'Einladung nicht verfügbar',
        invalidDescription:
            'Der Link ist abgelaufen, wurde widerrufen oder ist nicht mehr gültig. Wenden Sie sich an Ihren Unternehmensvertreter.',
        error: 'Die Anfrage ist fehlgeschlagen. Versuchen Sie es erneut.',
        retry: 'Erneut versuchen',
        loading: 'Einladung wird geladen…',
        successTitle: 'Prüfung abgeschlossen',
        successDescription: 'Ihr Testergebnis wurde gespeichert. Sie können diese Seite schließen.',
        roles: {
            admin: 'Geschäftsführer',
            beneficiary: 'Begünstigter',
            beneficiary_admin: 'Geschäftsführer und Begünstigter',
        },
    },
    kyc: {
        title: 'Dokumentenprüfung',
        openSection: 'Dokumentenprüfung öffnen',
        progress: 'Prüfstatus',
        progressCount: '{completed} von {total} erforderlichen Schritten abgeschlossen',
        refresh: 'Aktualisieren',
        hint: 'Laden Sie die fehlenden Dokumente hoch. Der Status wird anhand der Prüfergebnisse aktualisiert.',
        loading: 'Prüfung wird geladen…',
        retry: 'Erneut versuchen',
        empty: 'Derzeit sind keine Schritte verfügbar. Versuchen Sie es später erneut oder kontaktieren Sie den Support.',
        documents: 'Unternehmensdokumente',
        identity: 'Identitätsprüfung',
        otherChecks: 'Weitere Prüfungen',
        rejectedHint: 'Die Prüfung war nicht erfolgreich. Wenden Sie sich bei Bedarf an den Support.',
        openDocument: 'Dokument öffnen',
        upload: 'Datei hochladen',
        uploadAgain: 'Erneut hochladen',
        uploading: 'Wird hochgeladen…',
        uploadSaved: 'Das Dokument wurde gespeichert und zur Prüfung eingereicht.',
        continue: 'Weiter',
        successTitle: 'Unternehmensprüfung abgeschlossen',
        successDescription: 'Alle erforderlichen Prüfschritte wurden erfolgreich abgeschlossen.',
        done: 'Fertig',
        statuses: {
            completed: 'Geprüft',
            failed: 'Prüfung fehlgeschlagen',
            in_progress_auto: 'Automatische Prüfung',
            in_progress_manual: 'In Prüfung',
            upload: 'Upload erforderlich',
            waiting: 'Wartet auf Prüfung',
            notRequired: 'Nicht erforderlich',
        },
        errors: {
            emptyFile: 'Die ausgewählte Datei ist leer.',
            largeFile: 'Die Datei darf höchstens 10 MiB groß sein.',
            invalidFile: 'Wählen Sie eine PDF-Datei oder ein Bild im Format JPEG, PNG, GIF, BMP oder WebP.',
            conflict:
                'Der Schritt hat sich geändert. Die Daten werden aktualisiert; prüfen Sie die verfügbaren Aktionen.',
            upload: 'Der Upload konnte nicht bestätigt werden. Prüfen Sie die aktualisierte Liste vor einem erneuten Upload.',
            load: 'Die Prüfdaten konnten nicht aktualisiert werden. Versuchen Sie es erneut.',
        },
        stages: {
            kbo: {
                title: 'KBO-Auszug',
                description: 'Unternehmensdaten aus dem offiziellen Register.',
            },
            company_charter: {
                title: 'Gesellschaftssatzung',
                description: 'Aktuelle Satzung einschließlich aller Änderungen.',
            },
            ubo: {
                title: 'UBO-Register',
                description: 'Angaben zu den wirtschaftlich Berechtigten des Unternehmens.',
            },
            annual_report: {
                title: 'Jahresbericht',
                description: 'Finanzberichte für das letzte abgeschlossene Geschäftsjahr.',
            },
            admin_id: {
                title: 'Identität der Geschäftsführer',
                description: 'Gesamtstatus der Identitätsprüfung der aktuellen Geschäftsführer.',
            },
            beneficiaries: {
                title: 'Identität der wirtschaftlich Berechtigten',
                description: 'Gesamtstatus der Identitätsprüfung der wirtschaftlich Berechtigten.',
            },
            pep_individual: {
                title: 'Weitere Prüfung der Geschäftsführer',
                description: 'Diese Prüfung wird von unserem Team durchgeführt.',
            },
            pep_entity: {
                title: 'Weitere Unternehmensprüfung',
                description: 'Diese Prüfung wird von unserem Team durchgeführt.',
            },
            risk_report: {
                title: 'Abschließende Prüfung',
                description: 'Diese Prüfung wird von unserem Team durchgeführt.',
            },
            bank_account: {
                title: 'Bankverbindung',
                description: 'Gesamtstatus der Prüfung der Bankverbindung.',
            },
            other: {
                title: 'Weitere Prüfung',
                description: 'Prüfstatus des Unternehmens.',
            },
        },
    },
    common: {
        close: 'Schließen',
        ok: 'OK',
    },
    sidebar: {
        appTitle: 'Office Hof',
        menu: {
            mail: 'Post',
            office: 'Büro',
            payment: 'Zahlung',
            support: 'Support',
            profile: 'Profil',
            logout: 'Abmelden',
        },
    },
    login: {
        title: 'Bei Ihrem Konto anmelden',
        subtitle: 'Persönliches Konto',
        loadingData: 'Ihre Daten werden geladen',
        form: {
            emailLabel: 'Anmeldung',
            emailPlaceholder: 'E-Mail-Adresse',
            passwordLabel: 'Passwort',
            passwordPlaceholder: 'Passwort eingeben',
            rememberMe: 'Angemeldet bleiben',
            rememberMeTooltip: 'Anmeldedaten 3 Monate speichern',
            submitButton: 'Anmelden',
            submitButtonLoading: 'Anmeldung...',
            forgotPassword: 'Passwort vergessen?',
            noAccountText: 'Kein Konto?',
            registerLink: 'Registrieren',
            forgotPasswordEmailSubject: 'Bitte Passwort ändern',
            forgotPasswordEmailBody:
                'Bitte ändern Sie das Passwort für mein Konto:\nE-Mail: {email},\nUnternehmen: {company}.',
            forgotPasswordEmailRequiredTitle: 'E-Mail eingeben',
            forgotPasswordEmailRequiredMessage:
                'Bitte geben Sie zuerst die E-Mail-Adresse Ihres Kontos in das Feld oben ein und klicken Sie anschließend auf «Passwort vergessen?».',
            selectCompany: 'Unternehmen',
            passwordDisabledHint: 'Gültige E-Mail eingeben',
            recoveryTitle: 'Wiederherstellung',
            loginLabel: 'Anmeldung',
            companyLabel: 'Unternehmen',
            newPasswordLabel: 'Ihr neues Passwort',
            copyButton: 'Kopieren',
            savePasswordWarning: 'Merken Sie sich das neue Passwort!',
            recoveryFailedMessage:
                'Zugriff konnte nicht wiederhergestellt werden, bitte versuchen Sie es erneut',
            backToLogin: 'Zurück zur Anmeldung',
            confirmRecoveryMessage: 'Bestätigen Sie die Kontowiederherstellung',
            confirmRecoveryButton: 'Bestätigen',
        },
        validation: {
            emailRequired: 'E-Mail-Adresse eingeben',
            emailInvalid: 'Gültige E-Mail-Adresse eingeben',
            passwordRequired: 'Passwort eingeben',
            incorrectCredentials: 'Falsche Anmeldung oder Passwort',
            userNotFound: 'Benutzer nicht gefunden',
        },
        errors: {
            authError: 'Authentifizierungsfehler',
            invalidCredentials: 'Ungültige Anmeldung oder Passwort',
            tooManyAttempts: 'Zu viele Versuche, bitte später erneut versuchen',
            logoutError: 'Abmeldung fehlgeschlagen',
        },
    },
    registration: {
        title: 'Registrierung',
        emailLabel: 'E-Mail',
        emailPlaceholder: 'Geben Sie Ihre E-Mail ein',
        continueButton: 'Weiter',
        haveAccount: 'Bereits ein Konto?',
        signInLink: 'Anmelden',
        confirmTitle: 'E-Mail-Adresse bestätigen',
        confirmMessage: 'Geben Sie den Code ein, den wir an {email} gesendet haben',
        confirmBack: 'Zurück',
        changeEmail: 'Andere E-Mail verwenden',
        confirmSpamHint: 'Kein Code? Prüfen Sie den Spam-Ordner.',
        confirmRequestAgainIn: 'Einen neuen Code können Sie anfordern in: {time}',
        confirmRequestAgainButton: 'Erneut anfordern',
        validation: {
            emailRequired: 'E-Mail eingeben',
            emailInvalid: 'Geben Sie eine gültige E-Mail ein',
        },
        errors: {
            invalidEmail: 'Ungültige E-Mail',
            rateLimit: 'Zu viele Versuche. Bitte später erneut versuchen.',
            serverError: 'Serverfehler, bitte später erneut versuchen.',
            wrongCode: 'Falscher Code',
        },
        form: {
            stepSelectTariff: 'Wählen Sie einen Tarif',
            stepCompanyDetails: 'Geben Sie die Daten Ihres Unternehmens ein',
            stepAddressDetails: 'Geben Sie Ihre Postadresse ein',
            addressForwardingHint: 'Papierbriefe und Pakete werden an diese Adresse weitergeleitet',
            stepPersonalDetails: 'Geben Sie Ihre persönlichen Daten ein',
            stepComplete: 'Fertig!',
            next: 'Weiter',
            back: 'Zurück',
            finish: 'Fertig',
            stepSelectTariffStub: 'Hier erscheint die Tarifauswahl.',
            stepCompanyDetailsStub: 'Hier erscheinen die Felder zu Ihrem Unternehmen.',
            stepPersonalDetailsStub: 'Hier erscheinen die Felder zu Ihren persönlichen Daten.',
            stepCompleteStub:
                'Die Registrierung ist abgeschlossen. Tippen Sie auf Fertig, um sich anzumelden.',
            loadingRegCustomerData: 'Daten werden geladen',
            selectOfficeLabel: 'Büro auswählen',
            selectTariffLabel: 'Tarif auswählen',
            officeBrussels: 'Brüssel',
            officeFlanders: 'Flandern',
            companyNameLabel: 'Firmenname',
            companyNamePlaceholder: 'Firmenname eingeben',
            activityLabel: 'Tätigkeit',
            activityPlaceholder: 'Beschreiben Sie die Tätigkeit des Unternehmens',
            innLabel: 'USt-IdNr.',
            innPlaceholder: 'USt-IdNr. eingeben',
            promoCodeLabel: 'Promocode',
            promoCodePlaceholder: 'Promocode eingeben',
            ifAvailable: 'falls vorhanden',
            tariffDetailsLink: 'Mehr zu den Tarifen',
            registrationFor: 'Registrierung für',
            firstNameLabel: 'Vorname',
            firstNamePlaceholder: 'Vorname eingeben',
            lastNameLabel: 'Nachname',
            lastNamePlaceholder: 'Nachname eingeben',
            countryLabel: 'Land',
            countryPlaceholder: 'Land auswählen',
            cityLabel: 'Stadt',
            cityPlaceholder: 'Stadt eingeben',
            lineLabel: 'Straße und Hausnummer',
            linePlaceholder: 'Straße und Hausnummer eingeben',
            postcodeLabel: 'Postleitzahl',
            postcodePlaceholder: 'Postleitzahl eingeben',
            countryBelgium: 'Belgien',
            countryFrance: 'Frankreich',
            countryGermany: 'Deutschland',
            countryNetherlands: 'Niederlande',
            countryPoland: 'Polen',
            genderLabel: 'Geschlecht',
            genderMale: 'Männlich',
            genderFemale: 'Weiblich',
            personalAddressLabel: 'Adresse',
            personalAddressPlaceholder: 'Adresse eingeben',
            phoneLabel: 'Telefon',
            phonePlaceholder: 'Telefon eingeben',
            passportFileLabel: 'Pass-Scan',
            uploadPassportFrontButton: 'Vorderseite hochladen',
            uploadPassportReverseButton: 'Rückseite hochladen',
            uploadAnotherPassportFileButton: 'Andere Datei auswählen',
            passportReverseFileLabel: 'Rückseite des Reisepasses',
            passportFrontSideLabel: 'Vorderseite des Reisepasses',
            openUploadedFile: 'Datei öffnen',
            completeRegistrationTitle: 'Registrierung bestätigen',
            completeRegistrationMessage:
                'Nach der Bestätigung wird das ausgefüllte Formular zur Moderation gesendet.',
            completeRegistrationConfirmButton: 'Bestätigen',
            completeRegistrationCancelButton: 'Abbrechen',
            completeValidationFieldsTitle: 'Bitte prüfen Sie die folgenden Felder:',
            completeValidationFieldError: 'Bitte dieses Feld prüfen',
            completeValidationInnHint: 'Bitte prüfen Sie das USt-/INN-Feld im Schritt Firmendaten',
            moderationInProgressTitle: 'Ihre Daten werden geprüft',
            moderationInProgressDescription:
                'Die Prüfung dauert bis zu 24 Stunden. Danach senden wir eine E-Mail an {email} mit Login und Passwort für den Zugang zum persönlichen Konto.',
            moderationInProgressContact: 'Wenn Sie Fragen haben, kontaktieren Sie uns unter:',
            returnToWebsiteButton: 'Zurück zur Website',
            registerButton: 'Registrieren',
            requiredField: 'Pflichtfeld',
            minLengthTemplate: 'Mindestens {n} Zeichen',
            maxLengthTemplate: 'Maximal {n} Zeichen',
            phoneInvalid: 'Telefon darf nur Ziffern, + und Klammern enthalten',
            latinOnly: 'Bitte nur lateinische Zeichen verwenden',
        },
    },
    pages: {
        mail: {
            title: 'Post',
            forwardToHomeAddress: 'Postweiterleitung an meine Privatadresse einrichten',
            forwardToHomeAddressDescription:
                'Alle Briefe werden automatisch an die angegebene Adresse weitergeleitet',
            configureForwarding: 'Weiterleitung einrichten',
            incomingMailSettings: 'Einstellungen für das Postscannen',
            openQrCode: 'QR-Code-Schlüssel für Office Hof',
            orderForwarding: 'Postweiterleitung bestellen',
            forwarding: 'Weiterleitung von Briefpost',
            orderForwardingTitle: 'Postweiterleitung bestellen',
            orderOneTimeForwarding: 'Einmalige Weiterleitung bestellen',
            orderForwardingMessage:
                'Bestellen Sie die Postweiterleitung und wir senden alle Ihre Briefe vom Office Hof an Ihre Adresse',
            orderButton: 'Bestellen',
            orderForwardingSuccess: 'Weiterleitung bestellt',
            orderForwardingError: 'Fehler bei der Bestellung der Weiterleitung',
            forwardingSentMessage: 'Briefe wurden an Ihre Adresse weitergeleitet. Empfang bestätigen.',
            forwardingSentHint:
                'Wenn Briefe innerhalb von 10 Tagen nicht ankommen, kontaktieren Sie den Support',
            forwardingTrackNumber: 'Sendungsnummer',
            forwardingTrackOnBpost: 'Bei bpost verfolgen',
            forwardingReceivedButton: 'Weiterleitung erhalten',
            forwardingReceivedSuccess: 'Empfang der Weiterleitung bestätigt',
            forwardingReceivedError: 'Fehler bei der Empfangsbestätigung',
            forwardingEmptyMessage:
                'Weiterleitung storniert, da das Postfach zum Zeitpunkt der Anfrage leer war',
            singleForwardingStatus: {
                processing: 'In Bearbeitung',
                completed: 'Abgeschlossen',
                pending: 'Ausstehend',
                cancelled: 'Storniert',
                sent: 'Gesendet',
            },
            singleForwardingRequestDate: 'Anfragedatum',
            singleForwardingStatusLabel: 'Status',
            cancelButton: 'Abbrechen',
            qrCodeTitle: 'Persönlicher QR-Code',
            qrCodeDescription:
                'Mit diesem QR-Code erhalten Sie Zugang zum Büro, gebuchten Besprechungsräumen und Ihrem Postfach',
            qrCodeDescriptionWithAddress:
                'Mit diesem QR-Code erhalten Sie Zugang zum Büro unter {address}, gebuchten Besprechungsräumen und Ihrem Postfach',
            scanContent: 'Briefinhalt scannen',
            scanContentDescription: 'Inhalt eingehender Briefe automatisch scannen',
            scanningSettings: 'Scan-Einstellungen',
            scanningSettingsPaperStorageTooltip:
                'Die Papieroriginale der Briefe werden in Ihrem persönlichen Postfach innerhalb des Bürogebäudes aufbewahrt.',
            scanningDescription:
                'Wenn Sie aus Gründen der Vertraulichkeit nicht möchten, dass Ihre Briefpost von unseren Mitarbeitern geöffnet und gescannt wird, deaktivieren Sie bitte die untenstehende Funktion. Benachrichtigungen über den Posteingang bleiben kostenlos.',
            forwardingSettings: 'Weiterleitungseinstellungen',
            regularForwarding: 'Regelmäßige Weiterleitung',
            oneTimeForwarding: 'Einmalige Weiterleitung',
            forwardMail: 'Post weiterleiten',
            forwardNever: 'Nie',
            forwardOncePerMonth: 'Einmal pro Monat',
            forwardTwicePerMonth: 'Zweimal pro Monat',
            forwardingDescription: 'Eingehende Post wird an Ihre Privatadresse weitergeleitet: {address}',
            changeAddressHint: 'Für Adressänderung schreiben Sie an den Support',
            changeAddressButton: 'Adresse ändern',
            forwardingInfo: 'Sie können regelmäßige Weiterleitung einrichten oder einmalig bestellen',
            sendRegisteredMail: 'Als Einschreiben senden',
            settingsSaved: 'Einstellungen gespeichert',
            saveError: 'Fehler beim Speichern der Einstellungen',
            mailsList: 'Postliste',
            noMails: 'Keine Post',
            loading: 'Laden...',
            loadError: 'Fehler beim Laden der Post',
            unavailable: 'Nicht verfügbar',
            mailTypeLetter: 'Brief',
            mailTypeLetterNoScan: 'Brief (nicht gescannt)',
            mailTypeParcel: 'Paket',
            parcelTypeSmall: 'Klein',
            letterNoScanModalTitle: 'Brief wurde nicht gescannt',
            letterNoScanModalMessage:
                'Ihre Briefpost wird bei Office Hof aufbewahrt. Sie können sie selbst abholen oder eine Weiterleitung bestellen.',
            letterNoScanOrderButton: 'Weiterleitung bestellen',
            parcelTypeBig: 'Groß',
            viewButton: 'Ansehen',
            downloadButton: 'Herunterladen',
            paymentHintButton: 'Hinweis',
            paymentRequiredTitle: 'Zahlung erforderlich',
            paymentRequiredMessage: 'Sie müssen für einen neuen Zeitraum zahlen, um diese Post anzusehen',
            payButton: 'Zahlen',
            searchByDate: 'Nach Datum suchen',
            thisWeek: 'Diese Woche',
            thisMonth: 'Diesen Monat',
            parcelReceivedTitle: 'Sie haben ein {type} Paket erhalten',
            parcelReceivedBig: 'groß',
            parcelReceivedSmall: 'klein',
            parcelPickupAddress: 'Abholung im Büro unter der Adresse',
            okButton: 'OK',
            dateFrom: 'Von',
            dateTo: 'Bis',
            resetButton: 'Zurücksetzen',
            applyButton: 'Anwenden',
            dateFromAfterTo: 'Datum „Von“ darf nicht später als „Bis“ sein',
            dateToBeforeFrom: 'Datum „Bis“ darf nicht früher als „Von“ sein',
            clearFilter: 'Filter löschen',
        },
        office: {
            title: 'Bürozentrum',
            inDevelopment: 'In Entwicklung',
            meetingRooms: 'Besprechungsräume',
            meetingRoomsDescription: 'Besprechungsräume buchen und verwalten',
            coworking: 'Coworking',
            coworkingDescription: 'Coworking-Platz buchen',
            noCoworkingAvailable: 'Keine Coworking-Plätze verfügbar',
            capacity: 'Kapazität',
            people: 'Personen',
            fromPrice: 'Ab',
            perHour: 'pro Stunde',
            includingVat: 'inkl. MwSt.',
            exclVat: 'zzgl. MwSt.',
            book: 'Reservieren',
            location: 'Standort',
            selectLocation: 'Standort auswählen',
            allLocations: 'Alle Standorte',
            noRoomsAvailable: 'Keine Räume verfügbar',
            rooms: {
                charlemagne: {
                    name: '"Charlemagne" (Grün)',
                    description:
                        'Ausstattung: Medienprojektor, Whiteboard und Marker, Audiosystem, Konferenztelefon. Zusätzlich: Kaffee, Tee und Wasser während der Buchung kostenlos.',
                },
                ambiorix: {
                    name: '"Ambiorix" (Rot)',
                    description:
                        'Ausgestattet mit LED-Projektor und Display, Whiteboard und Marker, Audiosystem, Konferenztelefon. Zusätzlich: Kaffee, Tee und Wasser während der Buchung kostenlos.',
                },
            },
            myBookings: 'Meine Buchungen',
            noBookings: 'Sie haben noch keine Buchungen',
            bookingQrModalTitle: 'Zugang Besprechungsraum',
            bookingQrModalMessage: 'Zeigen Sie den QR-Code für den Zugang zum Besprechungsraum',
            showQrCode: 'QR-Code anzeigen',
            hours: 'h',
            minutes: 'Min',
            datePlaceholder: 'tt.mm.jjjj',
            duration: 'Dauer',
            cost: 'Kosten',
            insufficientFundsTitle: 'Unzureichendes Guthaben',
            insufficientFundsMessage: 'Sie haben unzureichendes Guthaben',
            insufficientFundsMessageWithAmount:
                'Sie haben unzureichendes Guthaben für die Buchung. Es fehlen €{amount} auf Ihrem Konto',
            showMore: 'Mehr',
            hide: 'Ausblenden',
            booking: 'Buchung',
            selectDate: 'Datum auswählen',
            noSlotsAvailable: 'Keine Zeitslots verfügbar',
            roomNotSelected: 'Kein Besprechungsraum ausgewählt',
            available: 'Verfügbar',
            booked: 'Gebucht',
            bookingSuccess: 'Besprechungsraum erfolgreich gebucht',
            bookingError: 'Fehler beim Erstellen der Buchung',
            startTime: 'Anfang',
            endTime: 'Ende',
            selectStartTime: 'Uhrzeit wählen',
            selectEndTime: 'Uhrzeit wählen',
        },
        payment: {
            title: 'Zahlung',
            showContract: 'Vertrag mit Office Hof',
            balance: 'Guthaben',
            extraChargesThisMonth: 'Zusätzliche Leistungen diesen Monat berechnet',
            extraChargesSectionTitle: 'Kosten diesen Monat für zusätzliche Leistungen',
            extraServiceTypes: {
                scan: 'Post scannen',
                parcels: 'Brief-/Paketversand',
                bookings: 'Besprechungsraum-Buchungen',
                tariff: 'Tarif',
                postal_fees: 'Postgebühren',
            },
            extraChargesTotalLabel: 'Gesamt',
            tariff: 'Tarif',
            nextChargeDate: 'Nächste Abbuchung: ',
            tariffNotActivated: 'Tarif wird nach der Zahlung aktiviert',
            replenishBalance: 'Guthaben aufladen',
            replenish: 'Aufladen',
            pay: 'Zahlen',
            paySubscriptionTitle: 'Abonnement bezahlen',
            paySubscriptionMessage: 'Diese Seite ist nach der Abonnementzahlung verfügbar',
            availableAfterPrepayment: 'Nach Vorauszahlung verfügbar',
            enterAmount: 'Betrag eingeben und Zahlungsart wählen',
            paymentCredited:
                'Zahlungen werden in der Regel sofort gutgeschrieben, in Einzelfällen bis zu 2 Tage',
            paymentScreenTitle: 'Zahlung',
            enterAmountForPayment: 'Zu zahlenden Betrag eingeben',
            amountForPayment: 'Zu zahlender Betrag',
            replenishBalanceOn: 'Guthaben aufladen um:',
            totalToPay: 'Gesamt zu zahlen:',
            payByRequisites: 'Per Überweisung zahlen',
            requisiteCompany: 'Unternehmen',
            payOnline: 'Online zahlen',
            payFor1Month: '1 Monat zahlen',
            payFor3Months: '3 Monate zahlen',
            payFor6Months: '6 Monate zahlen',
            payFor12Months: '1 Jahr zahlen',
            discount: 'Rabatt',
            discountOnTariff: 'Rabatt {discount}% auf den Tarif für {months} Monate!',
            discountOnTariffTooltip:
                'Bei Zahlung von €{amount} erhalten Sie {discount}% Rabatt auf den Tarif für {months} Monate!',
            customAmount: 'Eigener Betrag',
            minAmountError: 'Mindestzahlungsbetrag: {amount}€',
            serverError: 'Serverfehler, wir kümmern uns darum',
            chargesHistory: 'Abbuchungsverlauf',
            invoice: 'Rechnung vom',
            noInvoices: 'Keine Rechnungen gefunden',
            noTariff: 'Sie haben keinen Tarif',
            currentTariff: 'Aktuell',
            sameAsPreviousTariff: 'Alles aus {tariffName}, außerdem:',
            fromPrice: 'Ab',
            perMonth: 'pro Monat',
            withoutVat: 'zzgl. MwSt. 21 %',
            goToTariff: 'Tarif ändern',
            tariffUpgradeSubject: 'Anfrage von Kunde {customer_id} zur Tariferhöhung auf {tariff_name}',
            tariffUpgradeBody: 'Bitte erhöhen Sie meinen Tarif auf {tariff_name}',
            tariffFeatures: {
                legalAddress: 'Die rechtliche Adresse Ihres Unternehmens',
                receptionMails: 'Empfang Ihrer eingehenden Post (einschließlich Einschreiben) und Pakete',
                coffeeSoftDrinks: 'Kaffee und Getränke für Sie und Ihre Kunden im Besprechungsraum',
                access24_7: 'Zugang zu Ihren Informationen und Nachrichten 24/7',
                sharedFaxLine: 'Gemeinsame Fax-Leitung (wie normale Post behandelt)',
                mailScanning: 'Scan Ihrer eingehenden Post und Weiterleitung in Ihr Postfach täglich',
                freePages: 'Anzahl kostenloser Blätter (Scan und Weiterleitung A4 zweiseitig) im Monatstarif',
                personalBox: 'Persönliches Fach für Dokumente, Zugang 24/7',
                ipTelephony: 'IP-Telefonie inkl. Nebenstelle in der Office Hof IP-Anlage',
                freeMeetingHours: 'Anzahl kostenloser Buchungsstunden Besprechungsräume pro Monat',
                freeParcels: 'Anzahl kostenloser eingehender Pakete im Monatstarif',
                discountLevel:
                    'Rabattstufe bei Buchung von Besprechungsräumen und Arbeitsplätzen, Post-Scan und Paketbehandlung',
                brusselsPhoneLine: 'Brüsseler Direktleitung (auch andere Regionen/Länder) über IP-Anlage',
                phoneCallsManagement:
                    'Verwaltung eingehender und ausgehender Anrufe und Faxe mit dedizierter IP-Anlage',
            },
        },
        support: {
            title: 'Support kontaktieren',
            phone: 'Telefon',
            email: 'E-Mail',
            phoneHint: '9–18 Uhr, Werktags',
            writeButton: 'An Support schreiben',
            emailSubject: 'Anfrage von Kunde {customer_id}',
            editDataEmailSubject: 'Datenänderungsanfrage von Kunde {customer_id}',
            innEmailSubject: 'Information von Kunde {customer_id}',
            innEmailBody: 'Hallo!\n\nMeine USt-IdNr.: \n',
            innModalTitle: 'USt-IdNr. erforderlich',
            innModalMessage: 'Bitte geben Sie Ihre USt-IdNr. an — wir leiten sie zur Prüfung weiter.',
            innModalSubmitButton: 'Absenden',
            innModalLaterButton: 'Später',
            innModalCloseButton: 'Schließen',
            innRequiredModalTitle: 'Steuer-ID erforderlich',
            innRequiredModalMessage:
                'Die Frist von {n} Tagen zur Angabe Ihrer Steuer-ID ist abgelaufen. Geben Sie sie unten ein, um den Dienst weiterhin nutzen zu können.',
            innInputLabel: 'USt-IdNr.',
            innInputPlaceholder: 'USt-IdNr. eingeben',
            innInputRequired: 'Bitte geben Sie Ihre USt-IdNr. an',
            innResubmitButton: 'Erneut senden',
            innPendingTitle: 'USt-IdNr. wird geprüft',
            innPendingMessage:
                'Wir haben Ihre USt-IdNr. {tax_id} erhalten und zur Prüfung weitergeleitet. Bis zum Abschluss der Prüfung bleibt der Zugang zum Dienst eingeschränkt.',
            innPendingResubmitHint:
                'Die Prüfung dauert länger als gewöhnlich. Sie können die USt-IdNr. erneut senden.',
            innSubmitError:
                'Die USt-IdNr. konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.',
            innButton: 'USt-IdNr. mitteilen',
            loading: 'Laden...',
            copied: 'In Zwischenablage kopiert',
        },
        recovery: {
            title: 'Wiederherstellung',
            description:
                'Um den Zugang zum Konto {email} wiederherzustellen, klicken Sie auf die Schaltfläche unten.',
            descriptionWithCompany:
                'Um den Zugang zum Konto {email} des Unternehmens {company_name} wiederherzustellen, klicken Sie auf die Schaltfläche unten.',
            button: 'Wiederherstellen',
            successMessage: 'Wir haben Ihnen eine E-Mail mit einem Wiederherstellungslink gesendet.',
            successHint: 'Wenn die E-Mail nicht angekommen ist, versuchen Sie es später erneut.',
            invalidEmail: 'Ungültige E-Mail',
        },
        profile: {
            title: 'Profil',
            companyData: 'Unternehmensdaten',
            contactPerson: 'Ansprechpartner',
            mailingAddress: 'Postanschrift',
            settings: 'Einstellungen',
            companyName: 'Name',
            inn: 'USt-IdNr.',
            officeAddress: 'Büroadresse',
            name: 'Name',
            email: 'E-Mail',
            phone: 'Telefon',
            interfaceLanguage: 'Sprache der Oberfläche',
            editData: 'Daten bearbeiten',
            editDataMessage: 'Für Änderung Ihrer oder der Unternehmensdaten wenden Sie sich an den Support',
            logoutButton: 'Abmelden',
            loading: 'Profildaten werden geladen...',
            languageNames: {
                ru: 'Russisch',
                en: 'Englisch',
                fr: 'Französisch',
                nl: 'Niederländisch',
                de: 'Deutsch',
            },
        },
    },
    installPrompt: {
        title: 'App installieren',
        description: 'Installieren Sie die App für schnelleren und einfacheren Zugang zu Office Hof',
        install: 'Installieren',
        skip: 'Überspringen',
        androidTitle: 'Auf Android installieren',
        androidSteps: [
            'Browser-Menü öffnen (drei Punkte oben rechts)',
            '«Zum Startbildschirm hinzufügen» oder «App installieren» wählen',
            'Installation bestätigen',
        ],
        iosTitle: 'Auf iPhone installieren',
        iosSteps: [
            'Teilen-Button (Quadrat mit Pfeil) in Safari tippen',
            'Scrollen und «Zum Home-Bildschirm» wählen',
            '«Hinzufügen» tippen',
        ],
        close: 'Schließen',
    },
};
